import { Suspense, lazy, useEffect, useState, ComponentType } from "react";
import VendorLogin from "./pages/vendor/VendorLogin";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { VendorAuthProvider } from "@/hooks/useVendorAuth";
import { useVendorAuth } from "@/hooks/useVendorAuth";
import { useVendorFcmRegistration } from "@/hooks/useVendorFcmRegistration";
import { useVendorPushHandler } from "@/hooks/useVendorPushHandler";
import { NetworkGateProvider } from "@/hooks/useNetworkGate";
import { AppThemeProvider } from "@/hooks/useDriverTheme";
import { LanguageProvider } from "@/hooks/useLanguage";
import { Loader2 } from "lucide-react";
import { VendorErrorBoundary } from "@/components/VendorErrorBoundary";
import AppVersionGate from "@/components/AppVersionGate";

// Retry wrapper: auto-reloads on stale chunk errors
function lazyRetry(factory: () => Promise<{ default: ComponentType<any> }>) {
  return lazy(() =>
    factory().catch((err) => {
      const key = "chunk_reload";
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        window.location.reload();
      }
      throw err;
    })
  );
}

const NotFound = lazyRetry(() => import("./pages/NotFound"));

// Vendor App — VendorLogin is the entry route, kept eager so the first
// paint never waits on a second chunk fetch on low-RAM Android devices.
const VendorRegister = lazyRetry(() => import("./pages/vendor/VendorRegister"));
const VendorDashboard = lazyRetry(() => import("./pages/vendor/VendorDashboard"));
const VendorVehicles = lazyRetry(() => import("./pages/vendor/VendorVehicles"));
const VendorDrivers = lazyRetry(() => import("./pages/vendor/VendorDrivers"));
const VendorTrips = lazyRetry(() => import("./pages/vendor/VendorTrips"));
const VendorEarnings = lazyRetry(() => import("./pages/vendor/VendorEarnings"));
const VendorProfile = lazyRetry(() => import("./pages/vendor/VendorProfile"));
const VendorNotifications = lazyRetry(() => import("./pages/vendor/VendorNotifications"));
const VendorDocReupload = lazyRetry(() => import("./pages/vendor/VendorDocReupload"));
const VendorAbout = lazyRetry(() => import("./pages/vendor/VendorAbout"));
const VendorTerms = lazyRetry(() => import("./pages/vendor/VendorTerms"));
const VendorPrivacyPolicy = lazyRetry(() => import("./pages/vendor/VendorPrivacyPolicy"));
const VendorDeleteAccount = lazyRetry(() => import("./pages/vendor/VendorDeleteAccount"));
const VendorSettings = lazyRetry(() => import("./pages/vendor/VendorSettings"));
const VendorPushNotificationSettings = lazyRetry(() => import("./pages/vendor/VendorPushNotificationSettings"));
const FleetTrackingMap = lazyRetry(() => import("./pages/vendor/fleet/FleetTrackingMap"));
const VendorFleetManagement = lazyRetry(() => import("./pages/vendor/VendorFleetManagement"));
const VehiclePerformanceList = lazyRetry(() => import("./pages/vendor/performance/VehiclePerformanceList"));
const DriverPerformanceList = lazyRetry(() => import("./pages/vendor/performance/DriverPerformanceList"));
const VehiclePerformanceDetail = lazyRetry(() => import("./pages/vendor/performance/VehiclePerformanceDetail"));
const DriverPerformanceDetail = lazyRetry(() => import("./pages/vendor/performance/DriverPerformanceDetail"));

const queryClient = new QueryClient();

function PageLoader() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

function AppRoutes() {
  return (
    <main className="min-h-dvh overflow-x-hidden">
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Navigate to="/vendor/login" replace />} />
          <Route path="/login" element={<Navigate to="/vendor/login" replace />} />

          {/* Vendor App Routes */}
          <Route path="/vendor/login" element={<VendorLogin />} />
          <Route path="/vendor/register" element={<VendorRegister />} />
          <Route path="/vendor/dashboard" element={<VendorDashboard />} />
          <Route path="/vendor/vehicles" element={<VendorVehicles />} />
          <Route path="/vendor/drivers" element={<VendorDrivers />} />
          <Route path="/vendor/trips" element={<VendorTrips />} />
          <Route path="/vendor/earnings" element={<VendorEarnings />} />
          <Route path="/vendor/profile" element={<VendorProfile />} />
          <Route path="/vendor/notifications" element={<VendorNotifications />} />
          <Route path="/vendor/documents" element={<VendorDocReupload />} />
          <Route path="/vendor/about" element={<VendorAbout />} />
          <Route path="/vendor/terms" element={<VendorTerms />} />
          <Route path="/vendor/privacy" element={<VendorPrivacyPolicy />} />
          <Route path="/vendor/delete-account" element={<VendorDeleteAccount />} />
          <Route path="/vendor/settings" element={<VendorSettings />} />
          <Route path="/vendor/settings/push-notifications" element={<VendorPushNotificationSettings />} />
          <Route path="/vendor/fleet-tracking" element={<FleetTrackingMap />} />
          <Route path="/vendor/fleet-tracking/*" element={<Navigate to="/vendor/fleet-tracking" replace />} />
          <Route path="/vendor/fleet-management" element={<VendorFleetManagement />} />
          <Route path="/vendor/performance/vehicles" element={<VehiclePerformanceList />} />
          <Route path="/vendor/performance/vehicles/:vehicleId" element={<VehiclePerformanceDetail />} />
          <Route path="/vendor/performance/drivers" element={<DriverPerformanceList />} />
          <Route path="/vendor/performance/drivers/:driverId" element={<DriverPerformanceDetail />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </main>
  );
}

function VendorPushBoot() {
  const { vendor } = useVendorAuth();
  // Defer FCM/push plugin imports until after first paint. On low-RAM
  // Android cold-starts these were competing with chunk parsing and
  // pushing the WebView into LOW_MEMORY before the login screen
  // rendered.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 1500);
    return () => clearTimeout(t);
  }, []);
  if (!ready) return null;
  return <VendorPushBootInner vendorId={vendor?.id ?? null} />;
}

function VendorPushBootInner({ vendorId }: { vendorId: string | null }) {
  useVendorFcmRegistration(vendorId);
  useVendorPushHandler();
  return null;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <TooltipProvider>
        <AppThemeProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <NetworkGateProvider>
              <VendorAuthProvider>
                <VendorPushBoot />
                <VendorErrorBoundary>
                  <AppVersionGate>
                    <AppRoutes />
                  </AppVersionGate>
                </VendorErrorBoundary>
              </VendorAuthProvider>
            </NetworkGateProvider>
          </BrowserRouter>
        </AppThemeProvider>
      </TooltipProvider>
    </LanguageProvider>
  </QueryClientProvider>
);

export default App;
